import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { freshnessStatuses } from '@dbt-labs/dbt-dag';
import { CellContext, DropdownButton, DropdownOption, Link } from '@dbt-labs/sourdough';

import {
  FreshnessStatus,
  ResourceNodeType,
  SourceLineageNode,
} from '@/__generated__/graphql';
import { PageHeading } from '@/components/PageHeading';
import { NodeStatusCell } from '@/components/Table/NodeStatusCell';
import { OneLineHeader } from '@/components/Table/TableHeader';
import { RightAlignedTruncatedCell, TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudProject } from '@/context/DbtCloudProjectProvider';
import { useDiscoveryAllSources } from '@/hooks/discovery/appliedAllSources';
import { useAppliedMaterializedFields } from '@/hooks/discovery/appliedMaterializationFields';
import { useLoadAllPaginatedResults } from '@/hooks/discovery/loadAllPaginatedResults';
import { useSourceFreshness } from '@/hooks/discovery/sourceFreshness';
import { useLinkToExplorer } from '@/hooks/links';
import { useUrlProjectParams } from '@/hooks/params';
import { useEnvironmentScope } from '@/hooks/useEnvironmentScope';
import { PageInfo } from '@/reducers/usePaginatedQuery';
import { daysFromToday } from '@/util/date';
import { STALE_FRESHNESS_THRESHOLD_IN_DAYS } from '@/util/dbt/freshness';
import { maxFreshnessStatus } from '@/util/dbt/maxFreshnessStatus';
import { toTitleCase } from '@/util/string';
import { INITIAL_PAGE_SIZE, ResourceFilterTable } from '../ResourceFilterTable';

type TableData = {
  freshnessStatus?: FreshnessStatus;
  sourceName: string;
  totalTables: number;
  database: string | null;
  schema: string | null;
};

const ALL_DATABASES_VALUE = '__all__';
const ALL_SCHEMAS_VALUE = '__all__';
const FRESHNESS_NO_DATA = 'no_data';

const getTableData = (sources: SourceLineageNode[]): TableData[] => {
  const summaryMap = new Map<string, TableData>();
  sources.forEach((source: SourceLineageNode) => {
    const sourceName = source.sourceName;
    if (!sourceName) {
      return;
    }
    const summaryEntry = summaryMap.get(sourceName);
    if (summaryEntry) {
      summaryEntry.totalTables++;
    } else {
      summaryMap.set(sourceName, {
        totalTables: 1,
        sourceName: sourceName,
        database: source.database || null,
        schema: source.schema || null,
      });
    }
  });
  const summary = Array.from(summaryMap.values());
  return summary;
};

export const SourceCollectionFilterView = () => {
  const pageSize = INITIAL_PAGE_SIZE;
  const [numResultsDisplayed, setNumResultsDisplayed] = useState(INITIAL_PAGE_SIZE);
  const { environmentId } = useDbtCloudProject();
  const projectScope = useUrlProjectParams();
  const { userId } = useEnvironmentScope();
  const [sourceLineageQuery] = useDiscoveryAllSources({
    environmentId,
    userId: userId,
  });
  const getSourceCollectionPageLink = useLinkToExplorer('sourceCollectionFilter');
  const sourceNodes = sourceLineageQuery.data?.environment.applied?.lineage;
  const [searchParams, setSearchParams] = useSearchParams();
  const pendingData = useMemo(() => {
    if (!sourceNodes) return undefined;
    return getTableData(sourceNodes as SourceLineageNode[]);
  }, [sourceNodes]);
  const displayedSourceNames = useMemo(() => {
    const sourceNames =
      pendingData?.reduce<Set<string>>((acc: Set<string>, { sourceName }) => {
        acc.add(sourceName);
        return acc;
      }, new Set()) ?? [];
    return Array.from(sourceNames);
  }, [pendingData]);

  const freshnessQuery = useSourceFreshness({
    environmentId,
    userId,
    sourceNames: displayedSourceNames,
  });

  const freshnessData = useLoadAllPaginatedResults({
    paginatedQuery: freshnessQuery,
    loadingStrategy: 'waitForAllResults',
  }).data;

  const isLoadingMore = sourceLineageQuery.fetching;

  const freshnessFilter = searchParams.get('freshness');
  const freshnessMatchValue =
    freshnessFilter === FRESHNESS_NO_DATA ? undefined : freshnessFilter;

  const databaseFilter = searchParams.get('database');
  const schemaFilter = searchParams.get('schema');
  const shouldDisplayAllFreshnessStatuses = !freshnessFilter;
  const shouldDisplayAllDatabases = !databaseFilter;
  const shouldDisplayAllSchemas = !schemaFilter;
  const shouldDisplayAllItems =
    shouldDisplayAllFreshnessStatuses &&
    shouldDisplayAllDatabases &&
    shouldDisplayAllSchemas;

  const data = useMemo(() => {
    if (freshnessData === undefined) {
      return undefined;
    }
    const freshnessMap =
      freshnessData?.reduce<Record<string, FreshnessStatus | undefined>>(
        (acc, curr) => {
          if (
            !curr?.sourceName ||
            !curr?.freshness?.snapshottedAt ||
            daysFromToday(curr?.freshness?.snapshottedAt) >
              STALE_FRESHNESS_THRESHOLD_IN_DAYS
          ) {
            return acc;
          }

          acc[curr.sourceName] = maxFreshnessStatus(
            curr.freshness?.freshnessStatus,
            acc[curr.sourceName],
          );
          return acc;
        },
        {},
      ) ?? {};
    const unfilteredData = pendingData?.map(
      (data): TableData => ({
        ...data,
        freshnessStatus: freshnessMap[data.sourceName],
      }),
    );
    if (shouldDisplayAllItems) return unfilteredData;

    return unfilteredData?.filter((data) => {
      let value = true;
      if (value && !shouldDisplayAllFreshnessStatuses) {
        value &&= data.freshnessStatus?.toLowerCase() === freshnessMatchValue;
      }
      if (value && !shouldDisplayAllDatabases) {
        value &&= data.database?.toLowerCase() === databaseFilter?.toLowerCase();
      }
      if (value && !shouldDisplayAllSchemas) {
        value &&= data.schema?.toLowerCase() === schemaFilter?.toLowerCase();
      }
      return value;
    });
  }, [
    databaseFilter,
    freshnessMatchValue,
    freshnessData,
    pendingData,
    schemaFilter,
    shouldDisplayAllDatabases,
    shouldDisplayAllFreshnessStatuses,
    shouldDisplayAllItems,
    shouldDisplayAllSchemas,
  ]);
  const displayedData = useMemo(
    () => data?.slice(0, numResultsDisplayed),
    [data, numResultsDisplayed],
  );

  const hasNextPage = data?.length && numResultsDisplayed < data.length;
  const pageInfo: PageInfo = {
    hasNextPage: !!hasNextPage,
    totalCount: data?.length ?? 0,
    endCursor: '',
  };
  const isInitialLoad =
    (sourceLineageQuery.fetching || freshnessQuery.fetching) &&
    (sourceNodes === undefined || data === undefined);

  const statusFilterOptions = useMemo(
    () => [
      { label: 'All', value: 'all' },
      { label: 'No Data', value: FRESHNESS_NO_DATA },
      ...freshnessStatuses.map(
        (status): DropdownOption => ({ label: toTitleCase(status), value: status }),
      ),
    ],
    [],
  );

  const defaultFreshnessOption =
    statusFilterOptions.find((option) => option.value === freshnessMatchValue) ||
    statusFilterOptions[0];

  const materializedFields = useAppliedMaterializedFields({
    environmentId,
    userId,
    resourceTypes: [ResourceNodeType.Source],
  });

  const databaseOptions = useMemo(() => {
    const opts = [
      { label: 'All', value: ALL_DATABASES_VALUE },
      ...(materializedFields?.databases || []).map<DropdownOption>((name) => ({
        label: name,
        value: name,
      })),
    ];
    return opts;
  }, [materializedFields?.databases]);

  const defaultDatabase = searchParams.get('database') || ALL_DATABASES_VALUE;

  const schemaOptions = useMemo(() => {
    const opts = [
      { label: 'All', value: ALL_SCHEMAS_VALUE },
      ...(materializedFields?.schemas || []).map<DropdownOption>((name) => ({
        label: name,
        value: name,
      })),
    ];
    return opts;
  }, [materializedFields?.schemas]);

  const defaultSchema = searchParams.get('schema') || ALL_SCHEMAS_VALUE;

  return (
    <>
      <PageHeading className="mb-10">Sources</PageHeading>
      <div className="my-2 flex flex-row gap-2">
        <DropdownButton
          options={statusFilterOptions}
          name={'Status'}
          defaultOption={defaultFreshnessOption}
          listClassName="z-50"
          onChange={({ value }) => {
            if (typeof value !== 'string') return;
            if (value === 'all') {
              setSearchParams((p) => {
                p.delete('freshness');
                return p;
              });
            } else {
              setSearchParams((p) => {
                p.set('freshness', value);
                return p;
              });
            }
          }}
        />
        <DropdownButton
          options={databaseOptions}
          name={'Database'}
          listClassName="overflow-visible z-50"
          defaultOption={databaseOptions.find((o) => o.value === defaultDatabase)}
          onChange={({ value }) => {
            if (typeof value !== 'string') return;
            if (value === ALL_DATABASES_VALUE) {
              setSearchParams((p) => {
                p.delete('database');
                return p;
              });
            } else {
              setSearchParams((p) => {
                p.set('database', value);
                return p;
              });
            }
          }}
        />
        <DropdownButton
          options={schemaOptions}
          name={'Schema'}
          listClassName="overflow-visible z-50"
          defaultOption={schemaOptions.find((o) => o.value === defaultSchema)}
          onChange={({ value }) => {
            if (typeof value !== 'string') return;
            if (value === ALL_SCHEMAS_VALUE) {
              setSearchParams((p) => {
                p.delete('schema');
                return p;
              });
            } else {
              setSearchParams((p) => {
                p.set('schema', value);
                return p;
              });
            }
          }}
        />
      </div>
      <ResourceFilterTable
        pluralizedResultName="source collections"
        data={displayedData}
        columns={[
          {
            header: 'Name',
            accessorKey: 'sourceName',
            id: 'name',
            cell: ({
              getValue,
              row: {
                original: { sourceName },
              },
            }: CellContext<TableData, any>) => (
              <Link
                isInternal
                to={getSourceCollectionPageLink({
                  ...projectScope,
                  sourceName: sourceName,
                })}
              >
                {getValue()}
              </Link>
            ),
          },
          {
            header: 'Freshness status',
            accessorKey: 'freshnessStatus',
            size: 100,
            cell: NodeStatusCell,
          },
          {
            header: () => <OneLineHeader title="Table count" align="right" />,
            accessorKey: 'totalTables',
            size: 60,
            minSize: 120,
            cell: RightAlignedTruncatedCell,
            meta: {
              align: 'right',
            },
          },
          {
            header: 'Database',
            accessorKey: 'database',
            size: 60,
            minSize: 120,
            cell: TruncatedCell,
          },
          {
            header: 'Schema',
            accessorKey: 'schema',
            cell: TruncatedCell,
          },
        ]}
        pageInfo={pageInfo}
        isLoading={isInitialLoad}
        isLoadingMore={isLoadingMore}
        pageSize={pageSize}
        fetchMore={() => setNumResultsDisplayed((numResults) => numResults + pageSize)}
      />
    </>
  );
};
